import { useCallback, useEffect } from 'react';
import { Position } from '../types/types';

interface InteractionHandlersProps {
  mode: 'view' | 'measure' | 'photoIndex' | 'areaTags';
  photoIndexMode: boolean;
  isDragging: boolean;
  scale: number;
  position: Position;
  displayPhotoMarkers: any[];
  svgRef: React.RefObject<SVGSVGElement>;
  groupRef: React.RefObject<SVGGElement>;
  dragStartRef: React.MutableRefObject<Position | null>;
  lastDragPointRef: React.MutableRefObject<Position | null>;
  dragStartTimeRef: React.MutableRefObject<number>;
  touchStartDistanceRef: React.MutableRefObject<number | null>;
  touchStartScaleRef: React.MutableRefObject<number>;
  setIsDragging: (dragging: boolean) => void;
  setPosition: (position: Position) => void;
  setScale: (scale: number) => void;
  setSelectedMarkers: (markers: Set<number>) => void;
  setLastSelectedMarkerId?: (id: number | null) => void;
  setIsHorizontalConstrained: (constrained: boolean) => void;
  setIsVerticalConstrained: (constrained: boolean) => void;
  addMeasurementPoint: (point: Position) => void;
  addPhotoMarker: (point: Position) => void;
  deleteSelectedMarkers?: () => void;
  copySelectedMarkers?: () => void;
  pasteMarkers?: () => void;
  undo?: () => void;
  onUpdateMarkerAttributes?: (attributes: any) => void;
}

export const useInteractionHandlers = ({
  mode,
  photoIndexMode,
  isDragging,
  scale,
  position,
  displayPhotoMarkers,
  svgRef,
  groupRef,
  dragStartRef,
  lastDragPointRef,
  dragStartTimeRef,
  touchStartDistanceRef,
  touchStartScaleRef,
  setIsDragging,
  setPosition,
  setScale,
  setSelectedMarkers,
  setLastSelectedMarkerId,
  setIsHorizontalConstrained,
  setIsVerticalConstrained,
  addMeasurementPoint,
  addPhotoMarker,
  deleteSelectedMarkers,
  copySelectedMarkers,
  pasteMarkers,
  undo,
  onUpdateMarkerAttributes,
}: InteractionHandlersProps) => {

  const getScaledPoint = useCallback((e: React.MouseEvent): Position | null => {
    if (!svgRef.current || !groupRef.current) return null;

    const svgRect = svgRef.current.getBoundingClientRect();
    const transform = groupRef.current.getCTM();
    if (!transform) return null;

    const invertedTransform = transform.inverse();
    const point = svgRef.current.createSVGPoint();
    point.x = e.clientX - svgRect.left;
    point.y = e.clientY - svgRect.top;

    const transformedPoint = point.matrixTransform(invertedTransform);

    return {
      x: Math.round(transformedPoint.x),
      y: Math.round(transformedPoint.y),
    };
  }, [svgRef, groupRef]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (mode === 'view' || mode === 'measure' || mode === 'areaTags' || photoIndexMode) {
      if (
        e.target === svgRef.current ||
        e.target === groupRef.current ||
        (e.target as Element).tagName === 'image'
      ) {
        e.preventDefault();
        setIsDragging(false);
        dragStartTimeRef.current = Date.now();
        // Anchor point: clientX minus current position gives the fixed PDF coordinate.
        dragStartRef.current = {
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        };
        // lastDragPointRef kept for back-compat but no longer used for threshold.
        lastDragPointRef.current = { x: e.clientX, y: e.clientY };
      }
    }
  }, [mode, photoIndexMode, position, svgRef, groupRef, setIsDragging, dragStartTimeRef, lastDragPointRef, dragStartRef]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!dragStartRef.current) return;
    // Start dragging immediately on first movement — no dead zone.
    setIsDragging(true);
    setPosition({
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y,
    });
  }, [setIsDragging, setPosition, dragStartRef]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    lastDragPointRef.current = null;
    dragStartRef.current = null;
  }, [setIsDragging, lastDragPointRef, dragStartRef]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (mode === 'view' || mode === 'measure' || mode === 'areaTags' || photoIndexMode) {
      e.preventDefault();

      if (e.touches.length === 1) {
        const touch = e.touches[0];
        setIsDragging(false);
        dragStartTimeRef.current = Date.now();
        // Anchor-point approach: same as mouse drag.
        dragStartRef.current = {
          x: touch.clientX - position.x,
          y: touch.clientY - position.y,
        };
        lastDragPointRef.current = { x: touch.clientX, y: touch.clientY };
      } else if (e.touches.length === 2) {
        const touch1 = e.touches[0];
        const touch2 = e.touches[1];
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        touchStartDistanceRef.current = distance;
        touchStartScaleRef.current = scale;
        // Cancel any active pan when a second finger lands.
        dragStartRef.current = null;
        lastDragPointRef.current = null;
      }
    }
  }, [mode, photoIndexMode, position, scale, setIsDragging, dragStartTimeRef, lastDragPointRef, dragStartRef, touchStartDistanceRef, touchStartScaleRef]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1 && dragStartRef.current) {
      e.preventDefault();
      const touch = e.touches[0];
      // Same anchor-point math as the PDF editor — no sensitivity multiplier.
      setPosition({
        x: touch.clientX - dragStartRef.current.x,
        y: touch.clientY - dragStartRef.current.y,
      });
      lastDragPointRef.current = { x: touch.clientX, y: touch.clientY };
      setIsDragging(true);
    } else if (e.touches.length === 2 && touchStartDistanceRef.current !== null && touchStartScaleRef.current !== null) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);

      const scaleFactor = distance / touchStartDistanceRef.current;
      const newScale = Math.max(0.5, Math.min(5, touchStartScaleRef.current * scaleFactor));

      setScale(newScale);
    }
  }, [setPosition, setScale, setIsDragging, lastDragPointRef, dragStartRef, touchStartDistanceRef, touchStartScaleRef]);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault();

    if (e.touches.length === 0) {
      if (!isDragging && dragStartTimeRef.current) {
        const dragDuration = Date.now() - dragStartTimeRef.current;
        if (dragDuration < 200 && e.changedTouches.length > 0) {
          const touch = e.changedTouches[0];
          const target = document.elementFromPoint(touch.clientX, touch.clientY);

          if (target) {
            const clickEvent = new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true,
              clientX: touch.clientX,
              clientY: touch.clientY,
              screenX: touch.screenX,
              screenY: touch.screenY,
            });

            setTimeout(() => {
              target.dispatchEvent(clickEvent);
            }, 0);
          }
        }
      }

      setIsDragging(false);
      lastDragPointRef.current = null;
      dragStartRef.current = null;
      touchStartDistanceRef.current = null;
    } else if (e.touches.length === 1) {
      // One finger left — re-anchor the pan from the remaining touch.
      const touch = e.touches[0];
      lastDragPointRef.current = { x: touch.clientX, y: touch.clientY };
      dragStartRef.current = {
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      };
      touchStartDistanceRef.current = null;
    }
  }, [isDragging, position, setIsDragging, dragStartTimeRef, lastDragPointRef, dragStartRef, touchStartDistanceRef]);

  const handleSvgClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as Element;
    if (target.closest('.photo-marker') || target.closest('.unit-polygon')) {
      return;
    }

    const dragDuration = Date.now() - (dragStartTimeRef.current || 0);
    const wasQuickClick = dragDuration < 200;

    if (mode === 'measure' && !isDragging && wasQuickClick) {
      const point = getScaledPoint(e);
      if (point) {
        addMeasurementPoint(point);
      }
    } else if (photoIndexMode && !isDragging && wasQuickClick) {
      const point = getScaledPoint(e);
      if (point) {
        addPhotoMarker(point);
      }
    } else if (photoIndexMode && !isDragging) {
      // Clicked on background in photo index mode - clear selection
      setSelectedMarkers(new Set());
    }
  }, [mode, photoIndexMode, isDragging, getScaledPoint, addMeasurementPoint, addPhotoMarker, setSelectedMarkers, dragStartTimeRef]);

  const handleWheelZoom = useCallback((e: React.WheelEvent) => {
    e.preventDefault();

    if (!svgRef.current || !groupRef.current) return;

    const zoomSpeed = 0.1;
    const factor = e.deltaY > 0 ? 1 - zoomSpeed : 1 + zoomSpeed;
    const newScale = Math.max(0.5, Math.min(5, scale * factor));

    if (newScale === scale) return;

    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;

    const screenCTM = svg.getScreenCTM();
    if (!screenCTM) return;

    const svgPoint = pt.matrixTransform(screenCTM.inverse());

    const groupX = (svgPoint.x - position.x) / scale;
    const groupY = (svgPoint.y - position.y) / scale;

    const newPosition = {
      x: svgPoint.x - groupX * newScale,
      y: svgPoint.y - groupY * newScale,
    };

    setScale(newScale);
    setPosition(newPosition);
  }, [scale, position, setScale, setPosition, svgRef, groupRef]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (photoIndexMode) {
        // Undo with Ctrl+Z / Cmd+Z
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
          e.preventDefault();
          if (undo) {
            undo();
          }
        }
        
        // Select all markers with Ctrl+A / Cmd+A
        // BUT: Don't intercept if user is typing in an input/textarea
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'a') {
          const target = e.target as HTMLElement;
          const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
          
          if (!isInput) {
            e.preventDefault();
            const allMarkerIds = new Set(displayPhotoMarkers.map((m) => m.id));
            setSelectedMarkers(allMarkerIds);
            
            // Set the last selected marker to the last marker (for range selection)
            if (displayPhotoMarkers.length > 0 && setLastSelectedMarkerId) {
              const lastMarker = displayPhotoMarkers[displayPhotoMarkers.length - 1];
              setLastSelectedMarkerId(lastMarker.id);
            }
            
            // Update marker attributes to match first selected marker (if any)
            if (displayPhotoMarkers.length > 0 && onUpdateMarkerAttributes) {
              const firstMarker = displayPhotoMarkers[0];
              onUpdateMarkerAttributes({
                width: firstMarker.width,
                height: firstMarker.height,
                fontSize: firstMarker.fontSize,
                aspectRatio: firstMarker.width / firstMarker.height,
              });
            }
          }
        }
        
        // Copy selected markers with Ctrl+C / Cmd+C
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
          const target = e.target as HTMLElement;
          const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
          
          if (!isInput) {
            e.preventDefault();
            if (copySelectedMarkers) {
              copySelectedMarkers();
            }
          }
        }
        
        // Paste markers with Ctrl+V / Cmd+V
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
          const target = e.target as HTMLElement;
          const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
          
          if (!isInput) {
            e.preventDefault();
            if (pasteMarkers) {
              pasteMarkers();
            }
          }
        }
        
        // Delete selected markers with Delete or Backspace
        // BUT: Don't intercept if user is typing in an input/textarea
        if (e.key === 'Delete' || e.key === 'Backspace') {
          const target = e.target as HTMLElement;
          const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
          
          if (!isInput) {
            e.preventDefault();
            if (deleteSelectedMarkers) {
              deleteSelectedMarkers();
            }
          }
        }
      }

      if (mode === 'measure') {
        if (e.shiftKey && e.key.toLowerCase() === 'h') {
          setIsHorizontalConstrained(true);
        }
        if (e.shiftKey && e.key.toLowerCase() === 'v') {
          setIsVerticalConstrained(true);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'h') {
        setIsHorizontalConstrained(false);
      }
      if (e.key.toLowerCase() === 'v') {
        setIsVerticalConstrained(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [photoIndexMode, mode, displayPhotoMarkers, setSelectedMarkers, setIsHorizontalConstrained, setIsVerticalConstrained, deleteSelectedMarkers, undo]);

  // Mouse event listeners
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  return {
    handleMouseDown,
    handleSvgClick,
    handleWheelZoom,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    getScaledPoint,
  };
}; 